/**
 * @module Pagination
 * @author: UI architecture team @ZS
 */

import ComponentMixin from "../smart/component.m.js";

class Base extends HTMLElement {
    constructor(...args) {
        super(...args);
    }
}

/**
 * Pagination component
 * @extends HTMLElement
 * @example <zs-pagination class="zs-pagination"></zs-pagination>
 * @example let el = document.createElement('zs-pagination')
 */
export default class Pagination extends ComponentMixin(Base) {
    constructor(...args) {
        // Constructor caveat https://github.com/WebReflection/document-register-element/
        const _ = super(...args);
        return _;
    }

    setup() {
        super.setup();
        this._setDefaults();
        this.syncProp('itemsCount');
        this.syncProp('pageSize');
        this.syncProp('currentPage');
        this.syncProp('maxDisplayPages');
        this.syncProp('mode');
        this.syncProp('pageSizeForDropdown');
        this.syncProp('pageLimitToEnableFirstLastButtons');
        this.on(['click', 'change', 'pagechange', 'beforechange'], undefined, undefined, false, false);
    }
    /**
     * returns the template of the element
     */
    template() {
        return `${this.first()}${this.prev()}${this.addPages()}${this.next()}${this.last()}`;
    }
    /**
     * Renders the pagination element.
     */
    render() {
        if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
        }
        this.innerHTML = this.template();
    }

    static get observedAttributes() {
        return (super.observedAttributes || []).concat(['items-count', 'page-size', 'current-page', 'max-display-pages', 'onpagechange', 'onbeforechange', 'mode', 'page-size-for-dropdown', 'page-limit-to-enable-first-last-buttons']);
    }

    _setDefaults() {
        this.UNKNOWN = 9007199254740991;
        this._itemsCount = 245;
        this._pageSize = 15;
        this._currentPage = 1;
        this._maxDisplayPages = 5;
        this._mode = 'action';
        this._pageSizeForDropdown = 25;
        this._pageLimitToEnableFirstLastButtons = 10;
    }


    get pagesToDisplay() {
        return this.maxDisplayPages;
    }

    get startPage() {
        var delta, pageVal = this.currentPage - Math.floor(this.pagesToDisplay / 2);
        if (pageVal <= 0) {
            pageVal = 1;
        }

        // Adjust the start page if we have fewer to display
        if (this.totalPages != this.UNKNOWN) {
            delta = this.totalPages - this.pagesToDisplay - pageVal;
            if (delta < 0) {
                pageVal += delta + 1;
                if (pageVal < 1) {
                    pageVal = 1;
                }
            }
        }
        return pageVal;
    }
    get nextPage() {
        return this.currentPage + 1;
    }
    get prevPage() {
        return this.currentPage - 1;
    }
    get firstPage() {
        return 1;
    }
    get lastPage() {
        return this.totalPages;
    }

    get totalPages() {
        if ((this.itemsCount || this.itemsCount === 0) && this.pageSize > 0) {
            return Math.ceil(this.itemsCount / this.pageSize);
        } else {
            return this.UNKNOWN;
        }
    }

    /**
     * returns html of a link of pages in the template of the element
     */

    page(index, currentPage, totalPages, unknown) {
        var i, html = '';
        currentPage = currentPage || this.currentPage;
        totalPages = totalPages || this.totalPages;
        unknown = unknown || this.UNKNOWN;
        if (index == currentPage) {
            if (totalPages >= this.pageSizeForDropdown && totalPages != unknown) { // Add a drop down								
                // Could avoid recreation of the element
                html = '<select>'
                for (i = 1; i <= totalPages; i++) {
                    html += '<option value="' + i + '"' + (i == currentPage ? ' selected' : '') + '>' + i + '</option>'
                }
                html += '</select>';

                return '<span current="" page="' + index + '">' + html + '</span>';

            } else {
                return '<a page="' + index + '" current="">' + index + '</a>';
            }
        }
        return '<a page="' + index + '">' + index + '</a>';
    }

    /**
     * add link of pages in the template of the element
     */
    addPages() {
        var html = '';
        for (let i = this.startPage; i < this.startPage + this.pagesToDisplay; i++) {
            if (i <= this.totalPages) {
                html += this.page(i);
            } else {
                break;
            }
        }
        return html;
    }

    /**
     * returns html of a link used to return to previous page
     */
    prev(currentPage) {
        var disabled;
        currentPage = currentPage || this.currentPage;
        if (currentPage == 1) {
            disabled = true;
        }

        if (this.mode === 'action') {
            return `<button prev page="${currentPage - 1}" class="zs-button zs-fab" ${disabled ? 'disabled' : ''}><span class="zs-icon zs-icon-arrow-left"></span></button>`;
        }
        return '<a href="#" onclick="return false;" class="zs-icon zs-icon-large zs-icon-prev" page="' + (currentPage - 1) + '" prev=""' + (disabled && ' disabled') + '></a>';
    }
    /**
     * returns html of a link used to navigate to next page
     */
    next(currentPage, totalPages) {
        var disabled;
        currentPage = currentPage || this.currentPage;
        totalPages = totalPages || this.totalPages;
        if (currentPage >= totalPages) {
            disabled = true;
        }

        if (this.mode === 'action') {
            return `<button next page="${currentPage + 1}" class="zs-button zs-fab" ${disabled ? 'disabled' : ''}><span class="zs-icon zs-icon-arrow-right"></span></button>`;
        }

        return '<a href="#" onclick="return false;" class="zs-icon zs-icon-large zs-icon-next" page="' + (currentPage + 1) + '" next=""' + (disabled && ' disabled') + '></a>';
    }
    /**
     * returns html of a link used to navigate to first page
     */
    first(currentPage, totalPages) {
        currentPage = currentPage || this.currentPage;
        totalPages = totalPages || this.totalPages;
        if (totalPages < this.pageLimitToEnableFirstLastButtons) {
            return '';
        }

        if (this.mode === 'action') {
            return `<button page="1" first class="zs-button zs-fab" ${currentPage == 1 ? 'disabled' : ''}><span class="zs-icon zs-icon-arrow-first"></span></button>`;
        }

        if (currentPage == 1) { // don't display a goto first link if the first page is already selected
            return '<a page="1" first="" disabled="disabled" class="zs-icon zs-icon-large zs-icon-first"></a>';
        }
        return '<a page="1" first="" class="zs-icon zs-icon-large zs-icon-first"></a>';
    }
    /**
     * returns html of a link used to navigate to last page
     */
    last(currentPage, totalPages, unknown) {
        currentPage = currentPage || this.currentPage;
        totalPages = totalPages || this.totalPages;
        unknown = unknown || this.UNKNOWN;
        if (totalPages < this.pageLimitToEnableFirstLastButtons) {
            return '';
        }
        if (this.mode === 'action') {
            return (currentPage == totalPages) ? `<button last page="${totalPages}" disabled class="zs-button zs-fab"><span class="zs-icon zs-icon-arrow-last"></span></button>` : (totalPages == this.UNKNOWN) ? '' : `<button last page="${totalPages}" class="zs-button zs-fab"><span class="zs-icon zs-icon-arrow-last"></span></button>`;
        }
        if (currentPage == totalPages) { // don't display a goto last link if the last page is already selected
            return '<a last="" page="' + totalPages + '" disabled="disabled" class="zs-icon zs-icon-last zs-icon-large"></a>';;
        } else if (totalPages == this.UNKNOWN) { // don't display a goto last link if total number of pages is unknown
            return '';
        }
        return '<a last="" page="' + totalPages + '" class="zs-icon zs-icon-large zs-icon-last"></a>';
    }
    /**
     * change the page of the element as per new page
     */
    changePage(newPage) {
        this.fire('beforechange', {
            detail: {
                oldPage: this.currentPage
            }
        });
        var oldPage = this.currentPage;
        if (newPage > this.totalPages) {
            newPage = this.totalPages;
        } else if (newPage <= 0) {
            newPage = 1;
        }
        this.currentPage = newPage;
        this.fire('pagechange', {
            detail: {
                oldPage: oldPage,
                newPage: newPage
            }
        });
    }

    getPropertyType(name) {
        switch (name) {
            case 'items-count':
            case 'page-size':
            case 'current-page':
            case 'max-display-pages':
            case 'page-size-for-dropdown':
            case 'page-limit-to-enable-first-last-buttons':
                return 'number'
            case 'onpagechange':
            case 'onbeforechange':
                return 'function'
            default:
                return 'string';
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        this.syncAttr(name, newValue, this.getPropertyType(name));
    }

    propertyChangedCallback(name, oldValue, newValue) {
        this.mode === 'classic' ? console.warn("Classic mode has been deprecated since v5.0 and shall be removed in future release. Please use 'action' mode instead.") : null;

        super.propertyChangedCallback(name, oldValue, newValue);
        if (!this.parentNode && !this.innerHTML) {
            return;
        }

        var observableProps = ['itemsCount', 'pageSize', 'currentPage', 'maxDisplayPages', 'pageSizeForDropdown', 'pageLimitToEnableFirstLastButtons'];

        if (observableProps.indexOf(name) > -1) {
            if (this._renderDebounce) {
                window.cancelAnimationFrame(this._renderDebounce);
                delete this._renderDebounce;
            }
            var self = this;
            this._renderDebounce = window.requestAnimationFrame(function () {
                self.render();
                delete self._renderDebounce;
            });
        }

    }
    get lastPageLink() {
        return this.querySelector('[last]');
    }
    get firstPageLink() {
        return this.querySelector('[first]');
    }
    get nextPageLink() {
        return this.querySelector('[next]');
    }

    get prevPageLink() {
        return this.querySelector('[prev]');
    }

    get currentPageLink() {
        return this.querySelector('[current]');
    }

    onclick(event) {
        if (typeof super.onclick == 'function') {
            super.onclick();
        }
        var page = event.target.getAttribute('page');
        if (event.target == this) {
            return;
        }
        if (event.target == this.prevPageLink) {
            page = this.currentPage - 1 || 1;
        } else if (event.target == this.nextPageLink) {
            page = this.currentPage + 1;
        } else if (event.target == this.firstPageLink) {
            page = 1;
        } else if (event.target == this.lastPageLink) {
            page = this.totalPages;
        } else if (event.target.getAttribute('page')) {
            page = Number(page);
        } else {
            return;
        }
        this.changePage(page);
    }

    onchange(event) {
        if (event.target.tagName == 'SELECT' && event.target.parentElement.hasAttribute('current')) {
            this.changePage(Number(event.target.value));
        }
    }

    onpagechange(event) {

    }
    onbeforechange(event) {

    }
    connectedCallback() {
        if (typeof super.connectedCallback == 'function') {
            super.connectedCallback();
        }
        this.render();
    }

}
Pagination.is = 'zs-pagination';